/**
 * CygnusAgentConfig — Admin
 * Configuration LLM + personnalisation de l'agent Cygnus (unifié).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Sparkles, Save, Check, Loader, Zap } from 'lucide-react';
import {
  getCygnusSettings, upsertCygnusSettings, testCygnusConnection,
} from '../../api';

const PROVIDERS = [
  { value: 'claude',  label: 'Claude (Anthropic)', placeholder: 'claude-sonnet-4-6' },
  { value: 'openai',  label: 'OpenAI (GPT)',        placeholder: 'gpt-4o' },
  { value: 'mistral', label: 'Mistral AI',           placeholder: 'mistral-large-latest' },
  { value: 'gemini',  label: 'Gemini (Google)',      placeholder: 'gemini-2.0-flash' },
  { value: 'ollama',  label: 'Ollama (local)',       placeholder: 'qwen2.5:7b' },
  { value: 'cortex',  label: 'Cortex (Delogic)',     placeholder: 'ollama/qwen2.5:7b' },
];

const TONES = [
  { value: 'amical',        label: 'Amical',        description: 'Accessible et chaleureux (défaut)' },
  { value: 'professionnel', label: 'Professionnel', description: 'Précis et rigoureux' },
  { value: 'concis',        label: 'Concis',        description: 'Direct, va à l\'essentiel' },
  { value: 'neutre',        label: 'Neutre',        description: 'Factuel, sans affect particulier' },
];

const EMPTY = {
  llmProvider: 'claude', llmModel: '', llmApiKey: '', llmBaseUrl: '',
  agentTone: 'amical', agentWelcomeMessage: '', agentContext: '', agentRestrictions: '',
};

const CygnusAgentConfig = () => {
  const [form, setForm] = useState(EMPTY);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCygnusSettings();
      if (res.data) {
        setForm({
          llmProvider: res.data.llmProvider || 'claude',
          llmModel: res.data.llmModel || '',
          llmApiKey: '',
          llmBaseUrl: res.data.llmBaseUrl || '',
          agentTone: res.data.agentTone || 'amical',
          agentWelcomeMessage: res.data.agentWelcomeMessage || '',
          agentContext: res.data.agentContext || '',
          agentRestrictions: res.data.agentRestrictions || '',
        });
        setHasApiKey(res.data.hasApiKey || false);
      }
    } catch (e) {
      if (e.response?.status !== 404) setError('Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await upsertCygnusSettings({
        llmProvider: form.llmProvider,
        llmModel: form.llmModel || null,
        llmApiKey: form.llmApiKey || null,
        llmBaseUrl: form.llmBaseUrl || null,
        agentTone: form.agentTone || null,
        agentWelcomeMessage: form.agentWelcomeMessage || null,
        agentContext: form.agentContext || null,
        agentRestrictions: form.agentRestrictions || null,
      });
      setSaved(true); setHasApiKey(true);
      setForm(f => ({ ...f, llmApiKey: '' }));
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.response?.data?.error || e.message || 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await testCygnusConnection();
      setTestResult(res.data);
    } catch { setTestResult({ success: false, message: 'Erreur de connexion' }); }
    setTesting(false);
  };

  const currentProvider = PROVIDERS.find(p => p.value === form.llmProvider);
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400';
  const textareaCls = inputCls + ' resize-none';

  if (loading) {
    return <div className="flex justify-center py-12"><Loader className="animate-spin text-indigo-500" size={24} /></div>;
  }

  return (
    <div className="space-y-6">

      {/* ── Config LLM ───────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap size={16} className="text-indigo-500" />
            Cygnus — Configuration LLM
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-indigo-50 rounded-lg px-4 py-3 text-sm text-indigo-700 border border-indigo-100">
            <strong>Config indépendante d'Orion</strong> — Cygnus peut utiliser un provider différent (ex : Mistral pour le support, Claude pour les RH).
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Provider LLM</label>
            <div className="grid grid-cols-2 gap-2">
              {PROVIDERS.map(p => (
                <button key={p.value} onClick={() => setForm(f => ({ ...f, llmProvider: p.value, llmModel: '' }))}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    form.llmProvider === p.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
            <input className={inputCls} value={form.llmModel}
              onChange={e => setForm(f => ({ ...f, llmModel: e.target.value }))}
              placeholder={`Ex: ${currentProvider?.placeholder || 'nom-du-modèle'}`} />
          </div>

          {form.llmProvider !== 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé API {hasApiKey && <span className="text-xs font-normal text-green-600 ml-1">✓ configurée</span>}
              </label>
              <input type="password" className={inputCls} value={form.llmApiKey}
                onChange={e => setForm(f => ({ ...f, llmApiKey: e.target.value }))}
                placeholder={hasApiKey ? 'Laisser vide pour conserver la clé existante' : 'sk-...'} />
            </div>
          )}

          {form.llmProvider === 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Ollama</label>
              <input className={inputCls} value={form.llmBaseUrl}
                onChange={e => setForm(f => ({ ...f, llmBaseUrl: e.target.value }))}
                placeholder="http://ollama:11434" />
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button onClick={handleTest} disabled={testing}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              {testing ? <Loader size={13} className="animate-spin" /> : <Zap size={13} />}
              Tester la connexion
            </button>
            {testResult && (
              <span className={`text-xs ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {testResult.success ? '✓' : '✗'} {testResult.message}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Personnalisation ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles size={16} className="text-indigo-500" />
            Cygnus — Personnalisation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="bg-indigo-50 rounded-lg px-4 py-3 text-sm text-indigo-700 border border-indigo-100">
            Ces paramètres sont injectés dans chaque conversation de Cygnus. Adaptez son comportement à votre organisation.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ton de réponse</label>
            <div className="grid grid-cols-2 gap-2">
              {TONES.map(t => (
                <button key={t.value} onClick={() => setForm(f => ({ ...f, agentTone: t.value }))}
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    form.agentTone === t.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message d'accueil <span className="text-xs font-normal text-gray-400">— optionnel</span>
            </label>
            <input className={inputCls} value={form.agentWelcomeMessage}
              onChange={e => setForm(f => ({ ...f, agentWelcomeMessage: e.target.value }))}
              placeholder="Ex: Bonjour ! Comment puis-je vous aider avec SuiviPlus aujourd'hui ?" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contexte personnalisé <span className="text-xs font-normal text-gray-400">— informations sur votre organisation</span>
            </label>
            <textarea rows={4} className={textareaCls} value={form.agentContext}
              onChange={e => setForm(f => ({ ...f, agentContext: e.target.value }))}
              placeholder="Ex: Nous gérons 3 micro-crèches (Les Lucioles, Les Poussinets, Les Bambins). La plupart des utilisateurs accèdent à l'application depuis une tablette."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Restrictions <span className="text-xs font-normal text-gray-400">— ce que Cygnus ne doit pas faire</span>
            </label>
            <textarea rows={3} className={textareaCls} value={form.agentRestrictions}
              onChange={e => setForm(f => ({ ...f, agentRestrictions: e.target.value }))}
              placeholder="Ex: Ne pas répondre aux questions sans rapport avec l'application."
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Aperçu ───────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-gray-600">Aperçu — instructions de Cygnus</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-mono leading-relaxed">
{`Tu es Cygnus, l'assistant intégré de l'application SuiviPlus.
Tu n'as PAS accès aux données des structures.
${form.agentContext ? `\nCONTEXTE :\n${form.agentContext}` : ''}${form.agentRestrictions ? `\nRESTRICTIONS :\n${form.agentRestrictions}` : ''}
Réponds en français, de manière ${
  form.agentTone === 'amical' ? 'amicale et accessible' :
  form.agentTone === 'professionnel' ? 'précise et professionnelle' :
  form.agentTone === 'concis' ? 'concise et directe' : 'neutre et factuelle'
}.`}
          </pre>
        </CardContent>
      </Card>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition disabled:opacity-50"
          style={{ backgroundColor: '#6366f1' }}>
          {saving ? <Loader size={15} className="animate-spin" /> : saved ? <Check size={15} /> : <Save size={15} />}
          {saved ? 'Enregistré' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

export default CygnusAgentConfig;
